import { FunctionComponent } from "react";
import { getBookingFieldSettings } from "../../selectors/modules.js";
import { getActiveSite } from "../../selectors/sites.js";
import { useAppSelector } from "../../store/hooks.js";
import styles from "../../stylesheets/components/booking-module.module.css";
import moduleBaseStyles from "../../stylesheets/components/module-base.module.css";
import {
  BaseModuleProps,
  BookingModuleSettings,
  LegalNavId,
} from "../../types/index.js";
import {
  getActiveColorScheme,
  getFallbackLanguage,
  getSupportedLanguage,
  resolveLink,
} from "../../utils/utils.js";
import BookingWidget from "../BookingWidget.js";
import ModuleHeadings from "../ModuleHeadings.js";
import ModuleWithHeadings from "../ModuleWithHeadings.js";

interface Props extends BaseModuleProps<BookingModuleSettings> {}

const BookingModule: FunctionComponent<Props> = ({
  translatedModule: {
    id,
    settings: { textAlign, defaultOfferList, layout, license },
    translation: {
      languageId,
      settings: { title, subtitle },
    },
  },
  translatedModule,
  isFirstOnPage,
  isPreview,
}) => {
  const site = useAppSelector((state) => getActiveSite(state.sites));
  const pages = useAppSelector((state) => state.pages);
  const modules = useAppSelector((state) => state.modules);
  const scheme = useAppSelector((state) =>
    getActiveColorScheme(state.colorSchemes, site, translatedModule),
  );
  const fallbackLanguageId = getFallbackLanguage(site, languageId);

  const getURL = (legalNavId: LegalNavId) =>
    resolveLink({
      fallbackLanguageId,
      isPreview,
      languageId,
      pageId: pages.byAlias[legalNavId],
      pages,
    }).href;

  return (
    <ModuleWithHeadings
      title={title}
      subtitle={subtitle}
      id={id}
      className={styles.BookingModule}
      colors={{ background: scheme.main.background }}
    >
      <div className={moduleBaseStyles.Module__Wrapper}>
        <ModuleHeadings
          scheme={scheme}
          isFirstOnPage={isFirstOnPage}
          textAlign={textAlign}
          title={title}
          subtitle={subtitle}
        />
      </div>
      <BookingWidget
        settings={{
          propertyId: site.accommodation.id,
          lang: getSupportedLanguage(languageId, ["de", "it", "en", "fr"]),
          layout,
          defaultOfferList,
          license,
          colorScheme: scheme,
          fieldSettings: getBookingFieldSettings(modules, languageId),
          termsURL: getURL("terms"),
          privacyURL: getURL("privacy"),
        }}
      />
    </ModuleWithHeadings>
  );
};

export default BookingModule;
